// The items in a scanned box. One item is a plain card; several are a pick-one list.
// An item the server refuses stays visible, disabled, with its refusal sentence under it.

import React from 'react';
import type { BoxItem } from '../../Parts/Pulls/pullsApi';
import { FOCUS, MOTION } from '../../Requisitions/buttonStyles';
import SectionChip from '../../components/SectionChip';
import { itemName } from './sentences';

interface Props {
  items: BoxItem[];
  selectedId: string | null;
  /** False for an item this action cannot use; its refusal shows under it. */
  usable: (item: BoxItem) => boolean;
  onSelect: (id: string) => void;
}

const Facts: React.FC<{ item: BoxItem; muted?: boolean }> = ({ item, muted = false }) => {
  const tone = muted ? 'text-on-surface-variant' : 'text-on-surface';
  return (
    <>
      <span className={`block text-lg font-semibold leading-snug ${tone}`}>{itemName(item)}</span>
      {item.sku && item.display_name && (
        <span className="block font-mono text-sm text-on-surface-variant">{item.sku}</span>
      )}
      <span className={`mt-1 block text-base ${tone}`}>
        <span className="font-mono tabular-nums">{item.quantity_remaining}</span> left at{' '}
        {item.location.section_code ? (
          <>
            <SectionChip code={item.location.section_code} />
            {item.location.warehouse_name && <> {item.location.warehouse_name}</>}
          </>
        ) : (
          item.location.path || item.location.name
        )}
      </span>
    </>
  );
};

const BoxItemList: React.FC<Props> = ({ items, selectedId, usable, onSelect }) => {
  if (items.length === 1) {
    const only = items[0];
    return (
      <section aria-label={itemName(only)} className="rounded-xl bg-surface-container p-4">
        <Facts item={only} />
        {!usable(only) && only.refusal && <p className="mt-2 text-sm text-red-200">{only.refusal}</p>}
      </section>
    );
  }
  return (
    <ul role="radiogroup" aria-label="Items in this box" className="flex flex-col gap-2">
      {items.map((item) => {
        const ok = usable(item);
        const on = item.receipt_item_id === selectedId;
        const noteId = `refusal-${item.receipt_item_id}`;
        return (
          <li key={item.receipt_item_id}>
            <button
              type="button"
              role="radio"
              aria-checked={on}
              disabled={!ok}
              aria-describedby={!ok && item.refusal ? noteId : undefined}
              onClick={() => onSelect(item.receipt_item_id)}
              className={`w-full rounded-xl px-4 py-3 text-left np-state ${FOCUS} ${MOTION}
                ${on ? 'bg-surface-container ring-2 ring-primary/50' : 'bg-surface-container-low'}
                ${ok ? '' : 'cursor-not-allowed'}`}
            >
              <Facts item={item} muted={!ok} />
            </button>
            {!ok && item.refusal && (
              <p id={noteId} className="mt-1 px-1 text-sm text-red-200">{item.refusal}</p>
            )}
          </li>
        );
      })}
    </ul>
  );
};

export default BoxItemList;
